import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Heading,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  date as commonDate,
  error as commonError,
  finleyAssert,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { useGetHistoricalRecordsForReceivableLazyQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { AssetDetailsSummary } from './AssetDetailsSummary';
import { BackBreadcrumbLink } from './BackBreadcrumbLink';
import { BorrowingBaseSummaryChart } from './BorrowingBaseSummaryChart';
import { CollateralDataQuality } from './CollateralDataQuality';
import { DaysPastDueChart } from './DaysPastDueChart';
import { EligibilityTests } from './EligibilityTests';
import { ExcessConcentrationChart } from './ExcessConcentrationChart';
import { RepaymentHistory } from './RepaymentHistory';
import { getRecordsForDateRangeAndTimePeriod } from './utils';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactLazyQueryGQLType } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetAssignedReceivableQuery } from 'packages/loanProduct/requests/requests.gql.generated';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
} from 'packages/portfolio/ui/shared/portfolioChartUtils';
import { PortfolioTimePeriodSelectButtons } from 'packages/portfolio/ui/shared/PortfolioTimePeriodSelectButtons';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';

type ReceivableDetailsProps = {
  organizationId: string;
  isLenderPage: boolean;
  finleyReceivableId: string;
};
export const ReceivableDetails = ({
  organizationId,
  isLenderPage,
  finleyReceivableId,
}: ReceivableDetailsProps) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [
    loanProductMechanicForReceivable,
    setLoanProductMechanicForReceivable,
  ] = useState<CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities>();

  const [
    multiLoanProductConcentrationLimits,
    setMultiLoanProductConcentrationLimits,
  ] = useState<
    CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[]
  >();

  const [
    getCreditAgreement,
    { data: creditAgreementData },
  ] = useGetCurrentCreditAgreementFullArtifactLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: assignedReceivableData,
    loading: assignedReceivableDataLoading,
  } = useGetAssignedReceivableQuery({
    variables: {
      finleyReceivableId,
    },
    onCompleted: (data) => {
      getCreditAgreement({
        variables: {
          creditFacilityId: data.assignedReceivable.creditFacility.id,
        },
      });
    },
    onError: () => {
      navigate('/');
    },
  });

  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  const [minReportingDate, setMinReportingDate] = useState<Date>();
  const [maxReportingDate, setMaxReportingDate] = useState<Date>();

  const [originationDate, setOriginationDate] = useState<Date | null>(null);
  const [assignedAtDate, setAssignedAtDate] = useState<Date | null>(null);

  const [normalizedInitialBalance, setNormalizedInitialBalance] = useState<
    number | null
  >(null);

  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    ChartTimePeriod.week,
  );

  const [historicalRecords, setHistoricalRecords] = useState<any[]>();

  const [
    historicalRecordsForDateRangeAndTimePeriod,
    setHistoricalRecordsForDateRangeAndTimePeriod,
  ] = useState<any[]>();

  const [
    getHistoricalRecords,
  ] = useGetHistoricalRecordsForReceivableLazyQueryGQLType({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      const receivableRecords: any[] =
        JSON.parse(data.historicalRecordsForReceivable) ?? [];
      const formattedReceivableRecords = receivableRecords.map((record) => {
        const newRecord = {
          ...record,
          reportingDate: record.reportingdate,
          fileVersion: record.fileversion,
        };
        return newRecord;
      });
      setHistoricalRecords(formattedReceivableRecords);

      if (formattedReceivableRecords.length > 0) {
        const minRepDate = DateTime.fromISO(
          formattedReceivableRecords[formattedReceivableRecords.length - 1]
            .reportingdate,
        ).toJSDate();
        const maxRepDate = DateTime.fromISO(
          formattedReceivableRecords[0].reportingdate,
        ).toJSDate();
        const maxRepMinus6MonthsDate = DateTime.fromISO(
          formattedReceivableRecords[0].reportingdate,
        )
          .minus({ months: 6 })
          .toJSDate();

        setMinReportingDate(minRepDate);
        setMaxReportingDate(maxRepDate);
        setChartDataDateRange({
          chartDataStartDate:
            minRepDate > maxRepMinus6MonthsDate
              ? minRepDate
              : maxRepMinus6MonthsDate,
          chartDataEndDate: maxRepDate,
        });
      }

      setOriginationDate(
        formattedReceivableRecords.length > 0 &&
          formattedReceivableRecords[0]['rif.normalizedoriginationdate']
          ? DateTime.fromISO(
              formattedReceivableRecords[0]['rif.normalizedoriginationdate'],
            ).toJSDate()
          : null,
      );

      setAssignedAtDate(
        formattedReceivableRecords.length > 0
          ? DateTime.fromISO(
              formattedReceivableRecords[0]['af.assignedat'],
            ).toJSDate()
          : null,
      );

      setNormalizedInitialBalance(
        formattedReceivableRecords.length > 0 &&
          formattedReceivableRecords[0]['rif.normalizedinitialbalance']
          ? formattedReceivableRecords[0]['rif.normalizedinitialbalance']
          : null,
      );
    },
  });

  useEffect(() => {
    if (assignedReceivableData) {
      getHistoricalRecords({
        variables: {
          organizationId,
          finleyReceivableId,
          creditFacilityId:
            assignedReceivableData.assignedReceivable.creditFacility.id,
        },
      });
    }
  }, [
    assignedReceivableData,
    getHistoricalRecords,
    finleyReceivableId,
    organizationId,
  ]);

  useEffect(() => {
    if (
      assignedReceivableData &&
      creditAgreementData &&
      creditAgreementData.currentCreditAgreementFullArtifact
    ) {
      const {
        currentCreditAgreementFullArtifact: {
          creditAgreement,
          id: creditAgreementId,
        },
      } = creditAgreementData;

      let parsedCreditAgreement = null;

      try {
        parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreement,
        );
      } catch (err) {
        toast({
          ...errorToastConfig,
          id: 'errorGetCurrentCreditAgreementFullArtifact',
          title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
        });
        navigate('/');
      }

      finleyAssert(
        parsedCreditAgreement,
        new commonError.FinleyError(
          `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${assignedReceivableData.assignedReceivable.creditFacility.id}`,
          {
            metadata: {
              logMeta: {
                receivable: assignedReceivableData.assignedReceivable,
              },
            },
          },
        ),
      );

      const {
        borrowingBaseMechanics: {
          calcAgentMechanics: {
            loanProductMechanics,
            multiLoanProductConcentrationLimits: mlpConcentrationLimits,
          },
        },
      } = parsedCreditAgreement;

      const loanProductMechanicForReceivableObj = loanProductMechanics.find(
        (lpMechanic) =>
          lpMechanic.entity.id ===
          assignedReceivableData.assignedReceivable.loanProductId,
      );

      finleyAssert(
        loanProductMechanicForReceivableObj,
        new commonError.FinleyError(
          `Expect loan product mechanic to exist for loan product id ${assignedReceivableData.assignedReceivable.loanProductId}`,
          {
            metadata: {
              logMeta: {
                receivable: assignedReceivableData.assignedReceivable,
              },
            },
          },
        ),
      );
      setLoanProductMechanicForReceivable(loanProductMechanicForReceivableObj);
      setMultiLoanProductConcentrationLimits(mlpConcentrationLimits);
    }
  }, [
    assignedReceivableData,
    finleyReceivableId,
    creditAgreementData,
    toast,
    navigate,
  ]);

  useEffect(() => {
    if (historicalRecords) {
      setHistoricalRecordsForDateRangeAndTimePeriod(
        getRecordsForDateRangeAndTimePeriod(
          historicalRecords,
          chartDataDateRange.chartDataStartDate,
          chartDataDateRange.chartDataEndDate,
          selectedTimePeriod,
        ),
      );
    }
  }, [historicalRecords, chartDataDateRange, selectedTimePeriod]);

  const onSelectDateRange = (newDateRange: [Date | null, Date | null]) => {
    const [newStartDate, newEndDate] = newDateRange;

    if (newStartDate && newEndDate) {
      setChartDataDateRange({
        chartDataStartDate: newStartDate,
        chartDataEndDate: newEndDate,
      });
    }

    // if dates cleared, pass in the initial reporting date conditions
    if (!newStartDate && !newEndDate) {
      setChartDataDateRange({
        chartDataStartDate: chartsInitialStartDateJs,
        chartDataEndDate: chartsInitialEndDateJs,
      });
    }
  };

  // eslint-disable-next-line no-nested-ternary
  const latestRecord = historicalRecordsForDateRangeAndTimePeriod
    ? historicalRecordsForDateRangeAndTimePeriod.length > 0
      ? historicalRecordsForDateRangeAndTimePeriod[
          historicalRecordsForDateRangeAndTimePeriod.length - 1
        ]
      : null
    : undefined;

  return (
    <Box>
      {!assignedReceivableData || assignedReceivableDataLoading ? (
        <Skeleton
          data-testid="loading-portfolio-receivable-details-header"
          height="180px"
          width="100%"
        />
      ) : (
        <>
          <BackBreadcrumbLink
            label="Back to Receivables"
            path={`/${
              isLenderPage ? 'portfolio/' : 'credit-facility-receivables/'
            }${isLenderPage ? 'receivables?creditFacility=' : ''}${
              assignedReceivableData.assignedReceivable.creditFacility.id
            }`}
          />
          <Flex mt="32px" justifyContent="space-between" alignItems="center">
            <Flex direction="column" width="50%">
              <Heading as="h2">
                Receivable ID{' '}
                {assignedReceivableData.assignedReceivable.externalReceivableId}
              </Heading>
              <Text color="gray" mt="16px">
                Finley ID {assignedReceivableData.assignedReceivable.id}
              </Text>
            </Flex>
            <Flex width="45%" justifyContent="flex-end" gap={2}>
              <FullDateRangePicker
                onSelectDateRange={onSelectDateRange}
                currentDateRange={{
                  startDate: chartDataDateRange.chartDataStartDate,
                  endDate: chartDataDateRange.chartDataEndDate,
                }}
                minDate={minReportingDate}
                maxDate={maxReportingDate}
                width="100%"
              />
              <PortfolioTimePeriodSelectButtons
                currentSelectedTimePeriod={selectedTimePeriod}
                onSelectTimePeriod={setSelectedTimePeriod}
              />
            </Flex>
          </Flex>
        </>
      )}
      {latestRecord !== null && (
        <AssetDetailsSummary
          receivable={assignedReceivableData}
          latestRecord={latestRecord}
          loanProductMechanic={loanProductMechanicForReceivable}
          multiLoanProductConcentrationLimits={
            multiLoanProductConcentrationLimits
          }
          originationDate={originationDate}
          assignedAtDate={assignedAtDate}
        />
      )}
      {historicalRecordsForDateRangeAndTimePeriod &&
        historicalRecordsForDateRangeAndTimePeriod.length > 0 &&
        loanProductMechanicForReceivable &&
        multiLoanProductConcentrationLimits &&
        assignedReceivableData && (
          <>
            <DaysPastDueChart
              historicalRecords={historicalRecordsForDateRangeAndTimePeriod}
              selectedTimePeriod={selectedTimePeriod}
            />
            <BorrowingBaseSummaryChart
              historicalRecords={historicalRecordsForDateRangeAndTimePeriod}
              selectedTimePeriod={selectedTimePeriod}
            />
            <EligibilityTests
              historicalRecords={historicalRecordsForDateRangeAndTimePeriod}
              loanProductMechanic={loanProductMechanicForReceivable}
              creditFacilityId={
                assignedReceivableData.assignedReceivable.creditFacility.id
              }
            />
            <ExcessConcentrationChart
              historicalRecords={historicalRecordsForDateRangeAndTimePeriod}
              selectedTimePeriod={selectedTimePeriod}
              loanProductMechanic={loanProductMechanicForReceivable}
              multiLoanProductConcentrationLimits={
                multiLoanProductConcentrationLimits
              }
            />
            <CollateralDataQuality
              historicalRecords={historicalRecordsForDateRangeAndTimePeriod}
              loanProductMechanic={loanProductMechanicForReceivable}
              creditFacilityId={
                assignedReceivableData.assignedReceivable.creditFacility.id
              }
              creditFacilitySlug={
                assignedReceivableData.assignedReceivable.creditFacility.slug
              }
              selectedTimePeriod={selectedTimePeriod}
            />
            <RepaymentHistory
              organizationId={organizationId}
              finleyReceivableId={finleyReceivableId}
              selectedTimePeriod={selectedTimePeriod}
              initialBalance={normalizedInitialBalance}
            />
          </>
        )}
      {historicalRecordsForDateRangeAndTimePeriod &&
        historicalRecordsForDateRangeAndTimePeriod.length === 0 && (
          <Alert status="warning" mt="40px" borderRadius="6px">
            <AlertIcon />
            <AlertDescription fontWeight="semibold">
              {`No records for this receivable exist in the selected date range (${commonDate.dateToHumanDateString(
                chartDataDateRange.chartDataStartDate,
              )} - ${commonDate.dateToHumanDateString(
                chartDataDateRange.chartDataEndDate,
              )}).`}
            </AlertDescription>
          </Alert>
        )}
    </Box>
  );
};
